import { createFileRoute } from '@tanstack/react-router';
import { useMemo } from 'react';
import { EvaluationTypeIcon } from '@/app/components/features/evaluations/EvaluationTypeIcon';
import { getContractDetailsQuery } from '@/app/hooks/queries/useContracts';
import {
  Breadcrumb,
  BreadcrumbMenu,
  BreadcrumbMenuItem,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/app/ui/components/breadcrumb';
import {
  Header,
  HeaderSidechatButton,
  HeaderBackButton,
  HeaderNextNavButton,
  HeaderNav,
  HeaderSidebarTrigger,
  HeaderPrevNavButton,
  useHeaderNav,
} from '@/app/ui/components/header';
import {
  EvaluationTypeLabelsEnum,
  StandardDeviationsTypes,
  DocumentTypes,
} from '@/app/utils/constants';
import { ContractsIcon } from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react';
import { useTranslations } from 'use-intl';
import { StandardDeviationsDetailPageContent } from '@/app/components/features/evaluations/StandardDeviations/StandardDeviationsDetailPageContent';
import { useGetStandardDeviations } from '@/app/components/features/evaluations/analysis/Deviations/useDeviations';
import { groupDeviations } from '@/app/components/features/evaluations/analysis/Deviations/groupingUtils';
import { flattenStandardDeviationStatements } from '@/app/components/features/evaluations/analysis/Deviations/utils';
import { StandardDeviation } from '@/app/types/models';
import { useOrganizationKnowledgeBases } from '@/app/hooks/queries/useInternalKnowledgeBases';
import useDocumentsQuery from '@/app/components/shared/EntityDocuments/useDocumentsQuery';
import { useStandardDocumentNames } from '@/app/components/features/evaluations/analysis/Deviations/useStandardDocumentNames';
import type { DeviationsFlatRow } from '@/app/components/features/evaluations/StandardDeviations/StandardDeviationsPageContent';
import { getProjectDetailsQuery } from '@/app/hooks/queries/useProjects';
import { ProjectTypeIcon } from '@/app/components/features/projects/ProjectTypeIcon';
import { useSuspenseQuery } from '@tanstack/react-query';
import { use } from 'react';
import { AppContext } from '@/app/appContext';

export const Route = createFileRoute(
  '/_authed/project/$projectId/_layout/(evaluation)/contracts/$contractId/_layout/(main)/standard-deviations/$deviationId',
)({
  component: RouteComponent,
});

function RouteComponent() {
  const t = useTranslations('pages.contractHome');
  const tFeature = useTranslations('features.standardDeviations');
  const { contractId, projectId, deviationId } = Route.useParams();
  const { data: project } = useSuspenseQuery(getProjectDetailsQuery(projectId));
  const { data: evaluation } = useSuspenseQuery(getContractDetailsQuery(contractId));
  const { userMeta } = use(AppContext);
  const organizationId = userMeta?.organizationId || '';

  const { data: contractStandardKbs } = useOrganizationKnowledgeBases(organizationId);

  const kbById = useMemo(() => {
    const m = new Map<string, string>();
    for (const kb of contractStandardKbs ?? []) m.set(kb.id, kb.name);
    return m;
  }, [contractStandardKbs]);

  const { data } = useGetStandardDeviations({
    deviationsType: StandardDeviationsTypes.CONTRACT,
    id: contractId,
    enabled: true,
  });
  const items = data?.items;

  const { data: documentsData } = useDocumentsQuery({
    documentsType: DocumentTypes.CONTRACT,
    id: contractId,
    orderBy: 'CreatedAt',
    orderDirection: 'desc',
  });

  const flatStatements = useMemo(
    () => flattenStandardDeviationStatements(items as StandardDeviation[]),
    [items],
  );

  const { standardDocNameMap } = useStandardDocumentNames(flatStatements);

  const statementsWithDocNames = useMemo(() => {
    return flatStatements.map((statement) => {
      const evalDoc = documentsData?.find((doc) => doc.id === statement.evalDocId);
      const standardDocName = standardDocNameMap.get(statement.standardDocId);

      return {
        ...statement,
        evalDocName: evalDoc?.name || statement.evalDocName || '',
        standardDocName: standardDocName || statement.standardDocName || '',
      };
    });
  }, [flatStatements, documentsData, standardDocNameMap]);

  const allRows: DeviationsFlatRow[] = useMemo(() => {
    const rows: DeviationsFlatRow[] = [];
    let idx = 0;
    for (const st of statementsWithDocNames) {
      const kbId = st.standardEvalId;
      if (!kbId || !kbById.has(kbId)) {
        continue;
      }
      const standardName = kbById.get(kbId) ?? '';
      rows.push({
        index: idx++,
        statement: st.statement,
        standardName,
        kbId,
        item: st,
      });
    }
    return rows;
  }, [statementsWithDocNames, kbById]);

  const groupedDeviations = useMemo(() => groupDeviations(allRows, 'standards'), [allRows]);

  const allGroups = useMemo(
    () =>
      groupedDeviations.map((group, index) => ({
        id: String(index),
        group,
      })),
    [groupedDeviations],
  );

  const { handlePrevious, handleNext, isPreviousDisabled, isNextDisabled, currentIndex } =
    useHeaderNav(allGroups, deviationId, (deviationId) => ({
      to: '/project/$projectId/contracts/$contractId/standard-deviations/$deviationId',
      params: { projectId, contractId, deviationId },
    }));

  return (
    <>
      <Header>
        <HeaderBackButton
          to="/project/$projectId/contracts/$contractId/standard-deviations"
          params={{ projectId, contractId }}
        />
        <HeaderSidebarTrigger />
        <Breadcrumb>
          <BreadcrumbList>
            <BreadcrumbMenu>
              <BreadcrumbMenuItem
                to="/project/$projectId/contracts/$contractId"
                params={{ projectId, contractId }}
              >
                <EvaluationTypeIcon type={EvaluationTypeLabelsEnum.CONTRACT} />
                {evaluation.name}
              </BreadcrumbMenuItem>
              <BreadcrumbMenuItem to="/project/$projectId" params={{ projectId }}>
                <ProjectTypeIcon type={project.type} />
                {project.name}
              </BreadcrumbMenuItem>
            </BreadcrumbMenu>
            <BreadcrumbSeparator className="max-md:hidden" />
            <BreadcrumbItem className="md:hidden">
              <BreadcrumbLink
                to="/project/$projectId/contracts/$contractId"
                params={{ projectId, contractId }}
              >
                <EvaluationTypeIcon type={EvaluationTypeLabelsEnum.CONTRACT} />
                <span className="line-clamp-1">{evaluation.name}</span>
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator className="md:hidden" />
            <BreadcrumbItem className="max-md:hidden">
              <BreadcrumbLink
                to="/project/$projectId/contracts/$contractId"
                params={{ projectId, contractId }}
              >
                <HugeiconsIcon icon={ContractsIcon} />
                {t('title')}
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator className="max-md:hidden" />
            <BreadcrumbItem>
              <BreadcrumbLink
                to="/project/$projectId/contracts/$contractId/standard-deviations"
                params={{ projectId, contractId }}
              >
                {tFeature('title')}
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbPage className="tabular-nums">#{currentIndex + 1}</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>
        <HeaderNav>
          <HeaderPrevNavButton onClick={handlePrevious} disabled={isPreviousDisabled} />
          <HeaderNextNavButton onClick={handleNext} disabled={isNextDisabled} />
        </HeaderNav>
        <HeaderSidechatButton />
      </Header>
      <StandardDeviationsDetailPageContent
        evaluationId={contractId}
        evaluationType={StandardDeviationsTypes.CONTRACT}
        documentType={DocumentTypes.CONTRACT}
        deviationId={deviationId}
      />
    </>
  );
}
